// Motor de frases románticas y poesía de alta devoción
// Soporta categorías: "Frases para dedicar", "Frases para enamorar", "Poesías y poemas"
// Tipos: "Frase", "Poesía", "Poema"
// Modos de autor: "Con Autor" (grandes maestros literarios) y "Sin Autor" (dedicatorias directas)

export type PhraseMode = 'with-author' | 'without-author';

export type RomanticType = 'Frase' | 'Poesía' | 'Poema';

export type RomanticCategory =
  | 'Frases para dedicar'
  | 'Frases para enamorar'
  | 'Poesías y poemas';

export interface RomanticPhrase {
  id: string;
  text: string;
  type: RomanticType;
  category: RomanticCategory;
  author?: string;
}

// ========================================================
// 1. COLECCIÓN CON AUTOR (Autores Célebres)
// ========================================================
export const QUOTES_WITH_AUTHOR: Omit<RomanticPhrase, 'id'>[] = [
  // --- FRASES PARA DEDICAR (CON AUTOR) ---
  {
    text: 'Te quiero no por quien eres, sino por quien soy cuando estoy contigo. Te quiero por haber tocado mi corazón desbordado y haber descubierto en él la parte más luminosa y sincera de mi alma.',
    type: 'Frase',
    category: 'Frases para dedicar',
    author: 'Gabriel García Márquez',
  },
  {
    text: 'Estar contigo o no estar contigo es la única medida de mi tiempo. Ya no son las horas ni los calendarios los que marcan mis días, sino la certeza inquebrantable de encontrarte en cada uno de mis pensamientos.',
    type: 'Frase',
    category: 'Frases para dedicar',
    author: 'Jorge Luis Borges',
  },
  {
    text: 'Sé que voy a quererte sin preguntas, sé que vas a quererme sin respuestas. Te quiero con esa ternura serena que no exige condiciones, que solo agradece el milagro infinito de coincidir en esta vida.',
    type: 'Frase',
    category: 'Frases para dedicar',
    author: 'Mario Benedetti',
  },
  {
    text: 'Tus ojos son mi conjuro contra la mala jornada, mi refugio contra la prisa del mundo y la promesa más dulce de que, pase lo que pase, a tu lado todo vuelve a florecer.',
    type: 'Frase',
    category: 'Frases para dedicar',
    author: 'Mario Benedetti',
  },
  {
    text: 'Si tuviera una flor por cada vez que pienso en ti, podría caminar por mi jardín para siempre, perdiéndome entre los aromas y colores que solo tu recuerdo hace nacer en mi corazón.',
    type: 'Frase',
    category: 'Frases para dedicar',
    author: 'Alfred Tennyson',
  },
  {
    text: 'No te pido que seas el final de mi camino, te pido que seas el viaje entero. Te quiero para acompañarnos, para sostenernos las manos en las dudas y para celebrar cada latido que compartimos.',
    type: 'Frase',
    category: 'Frases para dedicar',
    author: 'Jaime Sabines',
  },
  {
    text: 'Si sé lo que es el amor, si sé lo que es el consuelo y la paz más profunda, es gracias a ti. Llegaste a mi existencia no para llenarla de promesas, sino para enseñarme lo sagrado que es amar con el alma entera.',
    type: 'Frase',
    category: 'Frases para dedicar',
    author: 'Hermann Hesse',
  },
  {
    text: 'Te quiero como para leerte cada noche como mi libro favorito, aprendiendo de memoria cada uno de tus suspiros, tus sonrisas inesperadas y la infinita magia con la que transformas mi mundo.',
    type: 'Frase',
    category: 'Frases para dedicar',
    author: 'Mario Benedetti',
  },
  {
    text: 'Andábamos sin buscarnos pero sabiendo que andábamos para encontrarnos. Como si dos ríos nacidos en cumbres lejanas supieran desde el primer deshielo que su destino era fundirse en el mismo mar.',
    type: 'Frase',
    category: 'Frases para dedicar',
    author: 'Julio Cortázar',
  },
  {
    text: 'Amar no es mirarse el uno al otro; es mirar juntos en la misma dirección, construyendo paso a paso un horizonte donde nuestros sueños y nuestras almas caminan a la par.',
    type: 'Frase',
    category: 'Frases para dedicar',
    author: 'Antoine de Saint-Exupéry',
  },
  {
    text: 'Tú eres mi sol, mi luna y todas mis estrellas; eres el aliento de mis amaneceres y la calma de mis noches. Contigo aprendí que el amor verdadero es el cielo más inmenso que se puede habitar.',
    type: 'Frase',
    category: 'Frases para dedicar',
    author: 'E. E. Cummings',
  },
  {
    text: 'Por una mirada, un mundo; por una sonrisa, un cielo; por un beso... ¡yo no sé qué te diera por un beso! Pero por toda una vida a tu lado, te entregaría gustoso mi alma y cada uno de mis días.',
    type: 'Frase',
    category: 'Frases para dedicar',
    author: 'Gustavo Adolfo Bécquer',
  },
  {
    text: 'No fue en mi oído donde me susurraste, sino en mi corazón. No fueron mis labios los que besaste, sino mi alma, dejándole una huella de ternura y devoción que el tiempo jamás podrá borrar.',
    type: 'Frase',
    category: 'Frases para dedicar',
    author: 'Judy Garland',
  },
  {
    text: 'En el amor no hay más que un camino: entregarse sin condiciones, sin reservas y con la certeza absoluta de que el bien del otro es la mayor felicidad que uno puede experimentar.',
    type: 'Frase',
    category: 'Frases para dedicar',
    author: 'León Tolstói',
  },
  {
    text: 'Cuando me miras, sé que todo en esta vida tiene un propósito divino. Reducir el universo a una sola persona y engrandecer a esa persona hasta el infinito: en eso consiste el verdadero amor.',
    type: 'Frase',
    category: 'Frases para dedicar',
    author: 'Victor Hugo',
  },
  {
    text: 'Te amo para amarte y no para ser amado, puesto que nada me complace tanto como verte a ti feliz, libre, radiante y sabiendo que en mi pecho siempre tendrás un hogar seguro.',
    type: 'Frase',
    category: 'Frases para dedicar',
    author: 'George Sand',
  },
  {
    text: 'En vano he luchado, pero ya no puedo más; mis sentimientos no pueden contenerse. Tienes que permitirme decirte con qué ardor te admiro y te amo, pues mi corazón es y siempre será tuyo.',
    type: 'Frase',
    category: 'Frases para dedicar',
    author: 'Jane Austen',
  },
  {
    text: 'Nunca amé a nadie del modo en que te amo a ti: con toda el alma, en cada respiro y con la devoción de quien encuentra en otra persona la redención más hermosa y pura de la vida.',
    type: 'Frase',
    category: 'Frases para dedicar',
    author: 'Fiódor Dostoyevski',
  },

  // --- FRASES PARA ENAMORAR (CON AUTOR) ---
  {
    text: 'Ven a dormir conmigo: no haremos el amor, él nos hará. Dejaremos que la noche nos envuelva y que nuestros cuerpos reconozcan esa patria compartida que inventamos cada vez que nos miramos.',
    type: 'Frase',
    category: 'Frases para enamorar',
    author: 'Julio Cortázar',
  },
  {
    text: 'En un beso sabrás todo lo que he callado durante días y noches. Todas las palabras que temblaron en mi garganta se disuelven en el instante sagrado en que tu boca roza la mía.',
    type: 'Frase',
    category: 'Frases para enamorar',
    author: 'Pablo Neruda',
  },
  {
    text: 'El alma que hablar puede con los ojos, también puede besar con la mirada, desarmando cualquier defensa y confesando en silencio todo el fuego que arde sin remedio en mi pecho.',
    type: 'Frase',
    category: 'Frases para enamorar',
    author: 'Gustavo Adolfo Bécquer',
  },
  {
    text: 'Quiero hacer contigo lo que la primavera hace con los cerezos: despertar la vida en cada rincón, cubrirnos de flores nuevas y renovar el milagro de amarnos cada vez con más fuerza.',
    type: 'Frase',
    category: 'Frases para enamorar',
    author: 'Pablo Neruda',
  },
  {
    text: 'Te amo sin saber cómo, ni cuándo, ni de dónde; te amo directamente sin problemas ni orgullo: así te amo porque no sé amar de otra manera, sino así de este modo en que no soy ni eres.',
    type: 'Frase',
    category: 'Frases para enamorar',
    author: 'Pablo Neruda',
  },
  {
    text: 'El amor no mira con los ojos, sino con el alma; y por eso pintan ciego al alado Cupido. Cuando estoy frente a ti, no es tu belleza terrenal lo que me desarma, sino la luz inmortal de tu espíritu.',
    type: 'Frase',
    category: 'Frases para enamorar',
    author: 'William Shakespeare',
  },
  {
    text: 'Solo con el corazón se puede ver bien; lo esencial es invisible para los ojos. Y lo esencial en mi vida eres tú: el lazo invisible que da sentido y belleza a todo lo que me rodea.',
    type: 'Frase',
    category: 'Frases para enamorar',
    author: 'Antoine de Saint-Exupéry',
  },
  {
    text: 'Nos amamos con un amor que era más que amor; un amor tan hondo, dulce y apasionado que los propios ángeles en el firmamento contemplaban con asombro nuestra inmensa dicha de estar juntos.',
    type: 'Frase',
    category: 'Frases para enamorar',
    author: 'Edgar Allan Poe',
  },
  {
    text: 'Cada corazón entona una canción incompleta hasta que otro corazón le susurra de vuelta. Quienes desean amar encuentran siempre su melodía; al roce de tu ternura, todo mi ser se transforma en poesía.',
    type: 'Frase',
    category: 'Frases para enamorar',
    author: 'Platón',
  },
  {
    text: 'El amor es la poesía de los sentidos y la sinfonía de dos voluntades que se funden en una sola melodía. Contigo he descubierto que amar es la obra de arte más sublime de la existencia.',
    type: 'Frase',
    category: 'Frases para enamorar',
    author: 'Honoré de Balzac',
  },
  {
    text: 'Todo lo que sé del amor es que el amor es todo lo que existe; es la fuerza que sostiene las estrellas y el suspiro que da vida a este corazón que late únicamente pensando en ti.',
    type: 'Frase',
    category: 'Frases para enamorar',
    author: 'Emily Dickinson',
  },
  {
    text: 'Basta una caricia de tu voz para borrar el invierno más crudo de mi pecho. Tienes el don de aquietar mis tempestades y encender en mis pupilas una primavera que no se apaga jamás.',
    type: 'Frase',
    category: 'Frases para enamorar',
    author: 'Alfonsina Storni',
  },
  {
    text: 'Te amo más que a mi propia piel, y aunque no me quieras de la misma manera, de todos modos me quieres, ¿no es verdad? Tu presencia es la luz con la que pinto cada instante de mi mundo.',
    type: 'Frase',
    category: 'Frases para enamorar',
    author: 'Frida Kahlo',
  },
  {
    text: 'Eres la respuesta a cada plegaria que he elevado en mis noches solitarias. Eres un cántico, un sueño, un susurro, y no sé cómo he podido vivir tanto tiempo sin ti a mi lado.',
    type: 'Frase',
    category: 'Frases para enamorar',
    author: 'Nicholas Sparks',
  },
  {
    text: 'La medida del amor es amar sin medida; entregarse sin calcular pérdidas ni ganancias, sabiendo que en la entrega total del corazón se halla la única y verdadera plenitud del alma.',
    type: 'Frase',
    category: 'Frases para enamorar',
    author: 'San Agustín',
  },
  {
    text: 'Tu boca en la mía es el misterio más dulce que el universo ha creado; un pacto silencioso donde se apagan todas las dudas del mundo y solo permanece el fuego suave de nuestro amor.',
    type: 'Frase',
    category: 'Frases para enamorar',
    author: 'Jaime Sabines',
  },

  // --- POESÍAS Y POEMAS (CON AUTOR) ---
  {
    text: 'Podrá nublarse el sol eternamente; podrá secarse en un instante el mar; podrá la muerte cubrirme con su fúnebre crespón; pero jamás en mí podrá apagarse la llama de tu amor.',
    type: 'Poema',
    category: 'Poesías y poemas',
    author: 'Gustavo Adolfo Bécquer',
  },
  {
    text: 'Para mi corazón basta tu pecho, para tu libertad bastan mis alas. Desde mi boca llegará hasta el cielo lo que estaba dormido sobre tu alma.',
    type: 'Poema',
    category: 'Poesías y poemas',
    author: 'Pablo Neruda',
  },
  {
    text: '¿Qué es poesía?, dices mientras clavas en mi pupila tu pupila azul. ¿Qué es poesía? ¿Y tú me lo preguntas? Poesía... eres tú.',
    type: 'Poesía',
    category: 'Poesías y poemas',
    author: 'Gustavo Adolfo Bécquer',
  },
  {
    text: 'Te quiero como para escuchar tu risa toda la noche y dormir en tu pecho, sin sombras y sin distancias, te quiero como para no dejarte ir jamás.',
    type: 'Poesía',
    category: 'Poesías y poemas',
    author: 'Mario Benedetti',
  },
  {
    text: 'Es hielo abrasador, es fuego helado, es herida que duele y no se siente, es un soñado bien, un mal presente, es un breve descanso muy cansado.',
    type: 'Poema',
    category: 'Poesías y poemas',
    author: 'Francisco de Quevedo',
  },
  {
    text: 'Yo no te amo como si fueras rosa de sal, topacio o flecha de claveles que propagan el fuego: te amo como se aman ciertas cosas oscuras, secretamente, entre la sombra y el alma.',
    type: 'Poema',
    category: 'Poesías y poemas',
    author: 'Pablo Neruda',
  },
  {
    text: 'Si el corazón pensara, dejaría de latir. Pero el mío late tan solo porque sabe que tú existes.',
    type: 'Poesía',
    category: 'Poesías y poemas',
    author: 'Fernando Pessoa',
  },
  {
    text: 'Amo tus ojos cuando el sol los baña, pero los amo más en el crepúsculo, cuando en su fondo el pensamiento calla y habla el amor.',
    type: 'Poesía',
    category: 'Poesías y poemas',
    author: 'Amado Nervo',
  },
  {
    text: 'Porque te tengo y no, porque te pienso, porque la noche está de ojos abiertos, porque la noche pasa y digo amor, porque has venido a recoger tu imagen y eres mejor que todas tus imágenes.',
    type: 'Poema',
    category: 'Poesías y poemas',
    author: 'Mario Benedetti',
  },
  {
    text: 'No sé tú, pero yo quisiera ser el aire que respiras, la sombra que te abriga y el latido que nunca se apaga en tu pecho.',
    type: 'Poesía',
    category: 'Poesías y poemas',
    author: 'Octavio Paz',
  },
  {
    text: 'Rima suave, beso lento, caricia que desata el viento: donde tú pisas florece la tierra, donde tú miras renace mi fe.',
    type: 'Poesía',
    category: 'Poesías y poemas',
    author: 'Federico García Lorca',
  },
  {
    text: 'En ti los ríos cantan y mi alma en ellos huye como tú lo desees y hacia donde tú quieras. Márcala con tu cruz y con tu vuelo.',
    type: 'Poema',
    category: 'Poesías y poemas',
    author: 'Pablo Neruda',
  },
  {
    text: 'Te desnudas igual que si estuvieras sola y de pronto descubres que estás acompañada por mí, que te miro como si fueras la primera mujer del mundo.',
    type: 'Poesía',
    category: 'Poesías y poemas',
    author: 'Mario Benedetti',
  },
  {
    text: 'Caminante, son tus huellas el camino y nada más; pero contigo en mi camino, no hay fatiga, solo paz.',
    type: 'Poema',
    category: 'Poesías y poemas',
    author: 'Antonio Machado',
  },
];

// ========================================================
// 2. COLECCIÓN SIN AUTOR (Dedicatorias directas e íntimas)
// ========================================================
export const QUOTES_WITHOUT_AUTHOR: Omit<RomanticPhrase, 'id'>[] = [
  // --- FRASES PARA DEDICAR (SIN AUTOR) ---
  {
    text: 'Prometo amarte sin prisa y sin pausas, en los días soleados y en las noches de tormenta. No importa cuántos caminos nos depare el destino: en cada vida, en cada universo y en cada instante que respire, volveré a buscar tus ojos para elegirte una y mil veces.',
    type: 'Frase',
    category: 'Frases para dedicar',
  },
  {
    text: 'No eres solo una parte hermosa de mi vida: eres la razón por la que cada amanecer tiene sentido y luz. A tu lado aprendí que el amor verdadero no pesa ni duele; solo cobija, sana y hace latir el pecho con una paz inquebrantable.',
    type: 'Frase',
    category: 'Frases para dedicar',
  },
  {
    text: 'Si me dieran a elegir entre todas las riquezas del mundo y un solo instante contemplando tu mirada, te elegiría a ti sin vacilar. Porque en tu sonrisa encontré el hogar que mi alma buscó durante tanto tiempo.',
    type: 'Frase',
    category: 'Frases para dedicar',
  },
  {
    text: 'A tu lado descubrí que el amor no se busca ni se inventa: se reconoce cuando dos almas hechas la una para la otra se vuelven a encontrar después de haber esperado toda una eternidad.',
    type: 'Frase',
    category: 'Frases para dedicar',
  },
  {
    text: 'Tus abrazos son el único lugar del mundo donde me siento a salvo de cualquier tormenta, donde el ruido se apaga y el universo entero se reduce a la respiración compartida de nuestros corazones.',
    type: 'Frase',
    category: 'Frases para dedicar',
  },
  {
    text: 'Gracias por convertir los días comunes en los recuerdos más extraordinarios de mi existencia. Por enseñarme con paciencia infinita y caricias sinceras que amar es el regalo más hermoso de la vida.',
    type: 'Frase',
    category: 'Frases para dedicar',
  },
  {
    text: 'Mi lugar favorito en todo el universo es justo donde nuestras manos se entrelazan. Allí donde las palabras sobran y donde la complicidad de nuestras miradas construye un mundo hecho solo para nosotros dos.',
    type: 'Frase',
    category: 'Frases para dedicar',
  },
  {
    text: 'No necesito nada más en esta vida si cuento con tu sonrisa al despertar y con tu voz deseándome buenas noches. Eres mi refugio, mi mayor orgullo y el motivo diario de mi alegría más genuina.',
    type: 'Frase',
    category: 'Frases para dedicar',
  },
  {
    text: 'Te elijo hoy con certeza absoluta, te elegiré mañana con devoción y te amaré siempre sin final. Eres la persona con la que quiero compartir cada risa, superar cada reto y celebrar la dicha de envejecer juntos.',
    type: 'Frase',
    category: 'Frases para dedicar',
  },
  {
    text: 'Cada segundo a tu lado es un regalo que guardo como el tesoro más sagrado en mi pecho. Gracias por amarme tal como soy, por impulsarme a ser mejor y por ser la bendición más linda de mi camino.',
    type: 'Frase',
    category: 'Frases para dedicar',
  },
  {
    text: 'Eres la casualidad más hermosa y el destino más perfecto que jamás soñé tener. Desde que llegaste a mi vida, hasta las cosas más sencillas brillan con un encanto que solo tu presencia sabe regalar.',
    type: 'Frase',
    category: 'Frases para dedicar',
  },
  {
    text: 'Contigo el amor no se desgasta con los días: florece con cada amanecer. Cada conversación contigo, cada abrazo sincero y cada mirada cómplice me confirma que mi destino siempre estuvo escrito a tu lado.',
    type: 'Frase',
    category: 'Frases para dedicar',
  },

  // --- FRASES PARA ENAMORAR (SIN AUTOR) ---
  {
    text: 'Hay sonrisas que iluminan una habitación, pero la tuya enciende el universo entero en mi pecho. Cada vez que sonríes con esa mirada pícara y dulce, siento que no hay nada imposible en este mundo.',
    type: 'Frase',
    category: 'Frases para enamorar',
  },
  {
    text: 'Basta que pronuncies mi nombre con esa dulzura tuya para que todo mi mundo se detenga. Es como si el tiempo se rindiera ante tu voz, y mi único anhelo fuera quedarme escuchándote por el resto de mis días.',
    type: 'Frase',
    category: 'Frases para enamorar',
  },
  {
    text: 'Tienes esa magia silenciosa de quien no necesita hacer nada especial para conquistarlo todo. Tu ternura, tu forma de caminar la vida y la bondad infinita de tu corazón me enamoran cada vez más profundamente.',
    type: 'Frase',
    category: 'Frases para enamorar',
  },
  {
    text: 'No sé qué misterio tienen tus ojos, pero cada vez que me miras olvido cualquier palabra y solo quiero amarte. Me pierdo en tu mirar con la certeza de quien por fin ha encontrado su verdadero norte.',
    type: 'Frase',
    category: 'Frases para enamorar',
  },
  {
    text: 'Me enamoré de ti no solo por lo hermosa que eres por fuera, sino por la calma infinita y la calidez con que acaricias mi alma. Estar contigo es descansar en un mar de ternura que nunca se agota.',
    type: 'Frase',
    category: 'Frases para enamorar',
  },
  {
    text: 'Si el amor tuviera rostro, tendría tu sonrisa; si tuviera voz, susurraría como tú al oído; y si tuviera un hogar, sería ese espacio perfecto entre tus brazos donde siempre deseo despertar.',
    type: 'Frase',
    category: 'Frases para enamorar',
  },
  {
    text: 'Quédate conmigo, porque tus besos son la única adicción de la que jamás querré curarme. En tus labios encontré la dulzura exacta que le hacía falta a mi vida para sentirse completa y bendecida.',
    type: 'Frase',
    category: 'Frases para enamorar',
  },
  {
    text: 'Eres el primer pensamiento que me roba suspiros al amanecer y el sueño más tierno que cobija mis noches. No hay instante en que tu recuerdo no dibuje en mi rostro la sonrisa más sincera del día.',
    type: 'Frase',
    category: 'Frases para enamorar',
  },
  {
    text: 'Me gustas completa: con tus risas inesperadas, tus silencios reflexivos, tus manías adorables y ese brillo único en tu mirar que tiene el poder de devolverle la primavera a mi corazón.',
    type: 'Frase',
    category: 'Frases para enamorar',
  },
  {
    text: 'Tus labios son el poema que siempre soñé aprender de memoria, y tu abrazo es el refugio donde cualquier miedo se desvanece. Enamorarme de ti fue la rendición más dulce e inevitable de toda mi vida.',
    type: 'Frase',
    category: 'Frases para enamorar',
  },
  {
    text: 'No eres solo un deseo ni una emoción pasajera: eres la devoción más pura, dulce y profunda de todo mi ser. Mirarte es recordar que el mundo aún guarda milagros para quienes se atreven a amar con el alma.',
    type: 'Frase',
    category: 'Frases para enamorar',
  },

  // --- POESÍAS Y POEMAS (SIN AUTOR) ---
  {
    text: 'Guardo en el silencio de mi alma el eco de tu voz, como guarda la noche el brillo de las estrellas, esperando el alba solo para mirarte una vez más.',
    type: 'Poesía',
    category: 'Poesías y poemas',
  },
  {
    text: 'Si el viento pudiera hablar, te diría lo que suspiro en su brisa; si las olas pudieran escribir, llenarían la arena con tu nombre; pero soy yo quien te ama, con un amor que vence al mar y al viento.',
    type: 'Poema',
    category: 'Poesías y poemas',
  },
  {
    text: 'Eres mi cielo en calma, mi puerto seguro, la caricia que desarma mi orgullo más oscuro. En tus brazos el tiempo no corre ni pesa, solo existe la paz de esta dulce promesa.',
    type: 'Poema',
    category: 'Poesías y poemas',
  },
  {
    text: 'Hay versos que nacen del papel y otros que nacen del alma. Los míos brotan cada vez que tu mano roza la mía y el mundo entero calla.',
    type: 'Poesía',
    category: 'Poesías y poemas',
  },
  {
    text: 'Dos latidos que aprendieron acompasarse sin ensayo, dos miradas que se hablaron sin decir palabra alguna. Así es nuestro amor: un milagro que no necesita explicación.',
    type: 'Poesía',
    category: 'Poesías y poemas',
  },
  {
    text: 'En el jardín de mis recuerdos eres la flor que nunca marchita, el rocío que despierta mi alegría y el aroma de amor que para siempre habita.',
    type: 'Poema',
    category: 'Poesías y poemas',
  },
  {
    text: 'Ni la distancia más larga podría enfriar lo que siento, porque llevo tu calor grabado a fuego en cada pensamiento.',
    type: 'Poesía',
    category: 'Poesías y poemas',
  },
  {
    text: 'Bajo este mismo cielo compartimos el latido, somos verso y melodía de un amor correspondido. Que pasen los años, que cambie el destino, mi hogar siempre será tu camino.',
    type: 'Poema',
    category: 'Poesías y poemas',
  },
];

// ========================================================
// 3. MOTOR PROCEDURAL DE VARIEDAD INFINITA (Sin repetición)
// ========================================================
const INTROS_DEDICAR = [
  'Prometo amarte sin prisa y sin condiciones en cada etapa de nuestro camino;',
  'En cada amanecer que despierto a tu lado doy gracias por el milagro de tu existencia,',
  'Si tuviera que elegirte de nuevo entre un millón de vidas y de destinos posibles,',
  'Tu presencia en mi vida es el regalo más hermoso y el remanso más dulce que jamás soñé;',
  'Mi corazón encontró su verdadero hogar y su paz más absoluta desde que te conoció,',
  'Basta una caricia de tus manos y el refugio cálido de tu abrazo para comprender',
  'En este universo inmenso y a menudo caótico, tenerte a ti es mi mayor certeza,',
  'Te amo con la devoción pura y serena de quien ha encontrado su mitad predilecta,',
  'Mi mayor tesoro en esta existencia no se mide en bienes, sino en cada momento compartido a tu lado,',
  'Desde el bendito día en que cruzaste mi camino supe que el amor de verdad existía,',
];

const CORES_DEDICAR = [
  'porque en ti descubrí la calma que tanto anhelaba mi alma y el fuego que renueva mis ilusiones',
  'sabiendo que tu corazón y el mío fueron moldeados para complementarse en perfecta armonía',
  'donde el tiempo se detiene ante la inmensidad de esta complicidad que crece día tras día',
  'abrazando tus anhelos con la misma pasión con la que cuido de tu sonrisa y de tu felicidad',
  'recorriendo juntos cada sendero que la vida nos depare, sin soltarnos de la mano jamás',
  'iluminando hasta el rincón más oculto de mi existencia con esa dulzura que solo tú posees',
  'construyendo un refugio eterno donde siempre habrá ternura, respeto y cariño incondicional',
  'custodiando tu mirada y tus sueños como el compromiso más sagrado de mi corazón',
  'sintiendo que cada latido lleva grabado tu nombre con letras doradas de gratitud',
  'descubriendo la belleza de existir en tu mirada y la alegría pura de saberte feliz',
];

const OUTROS_DEDICAR = [
  'y por eso te elijo hoy, mañana y en cada una de las vidas que nos toque compartir juntos.',
  'porque nada en este universo se compara a la inmensa bendición de despertar y saberte a mi lado.',
  'para siempre, con la ternura más profunda, la lealtad más firme y la devoción más pura de mi ser.',
  'y prometo cuidar de este amor tan hermoso como la joya más sagrada y delicada de mi destino.',
  'porque contigo encontré esa felicidad sincera que el mundo entero nunca pudo otorgarme.',
  'sin condiciones, sin temores y con la convicción total de que nacimos para amarnos.',
  'hasta que las estrellas se apaguen y aún mucho más allá de las fronteras del infinito.',
  'y no existe mayor anhelo en mi pecho que arrancarte una sonrisa en cada instante de nuestros días.',
  'porque tú eres, fuiste y por siempre serás el capítulo más maravilloso y amado de mi historia.',
  'en un viaje inolvidable donde cada paso contigo es una bendición que atesoro con el alma.',
];

const INTROS_ENAMORAR = [
  'Me enamora cada gesto tuyo, tu manera tan dulce de hablar y la complicidad que nace con solo mirarnos;',
  'Hay una luz mágica e irresistible en tu sonrisa que disipa cualquier sombra de mi camino,',
  'No existe poesía en el mundo que alcance a describir lo que siento cuando me miras a los ojos,',
  'Cada vez que te acercas y me regalas ese brillo único de tus pupilas, mi mundo entero da un vuelco,',
  'Tus besos tienen el poder sagrado de aquietar todas mis dudas y encender una primavera interminable en mi pecho;',
  'Basta oír tu risa espontánea para que mi corazón olvide cualquier cansancio y se llene de gozo,',
  'Eres el deseo más dulce que guarda mi memoria y el sueño más hermoso que acaricia mis noches;',
  'Desde el primer instante en que sentí la magia de tu presencia, mi alma quedó prendada de ti;',
];

const CORES_ENAMORAR = [
  'porque despiertas en mí los sentimientos más puros, intensos y hermosos que un ser humano puede experimentar',
  'transformando los días rutinarios en momentos inolvidables llenos de pasión y ternura compartida',
  'haciendo que mi corazón lata con una fuerza apasionada que jamás creí posible sentir por nadie',
  'regalándome la certeza indiscutible de que el amor más profundo existe cuando dos almas se reconocen',
  'envolviendo mi alma en una calidez embriagadora que hace que cada segundo a tu lado sea un deleite',
  'borrando cualquier tristeza con tu risa y llenando de colores vivos todo mi universo',
];

const OUTROS_ENAMORAR = [
  'y no hay nada en este mundo que desee más que perderme en tus brazos y quedarme a vivir en tu piel.',
  'porque amarte y ser correspondido por ti es, sin duda alguna, el mayor milagro de toda mi existencia.',
  'y cada segundo que compartimos no hace más que confirmar que volvería a enamorarme de ti una y mil veces.',
  'dejando en mi corazón una huella imborrable que atesoro con fervor y veneración absoluta.',
  'en una historia de amor apasionada y eterna que desafía al tiempo y no conocerá jamás un final.',
];

const POETIC_VERSES = [
  {
    text: 'Cielo bordado de estrellas y un suspiro en la brisa: así es pensar en ti cada noche antes de dormir, sintiendo tu calor a pesar de la distancia.',
    type: 'Poesía' as RomanticType,
  },
  {
    text: 'Si fueras mar, yo sería arena para abrazar tus olas; si fueras noche, yo sería luna para iluminar tu calma.',
    type: 'Poesía' as RomanticType,
  },
  {
    text: 'Verso a verso, latido a latido, escribimos la historia más hermosa. No hay final en este libro, solo páginas doradas de amor eterno.',
    type: 'Poema' as RomanticType,
  },
  {
    text: 'Tus ojos son el puerto donde descansan mis tempestades; tu pecho, el santuario donde renace mi esperanza.',
    type: 'Poema' as RomanticType,
  },
  {
    text: 'Un rayo de sol que acaricia la mañana no es más tibio que tu recuerdo en mi corazón cuando despierto.',
    type: 'Poesía' as RomanticType,
  },
];

// Generador procedural sin autor para garantizar infinitud sin repetir
export function generateProceduralItem(category: RomanticCategory): RomanticPhrase {
  let text = '';
  let type: RomanticType = 'Frase';

  if (category === 'Frases para dedicar') {
    const intro = INTROS_DEDICAR[Math.floor(Math.random() * INTROS_DEDICAR.length)];
    const core = CORES_DEDICAR[Math.floor(Math.random() * CORES_DEDICAR.length)];
    const outro = OUTROS_DEDICAR[Math.floor(Math.random() * OUTROS_DEDICAR.length)];
    text = `${intro} ${core}, ${outro}`;
    type = 'Frase';
  } else if (category === 'Frases para enamorar') {
    const intro = INTROS_ENAMORAR[Math.floor(Math.random() * INTROS_ENAMORAR.length)];
    const core = CORES_ENAMORAR[Math.floor(Math.random() * CORES_ENAMORAR.length)];
    const outro = OUTROS_ENAMORAR[Math.floor(Math.random() * OUTROS_ENAMORAR.length)];
    text = `${intro} ${core}, ${outro}`;
    type = 'Frase';
  } else {
    const sample = POETIC_VERSES[Math.floor(Math.random() * POETIC_VERSES.length)];
    text = sample.text;
    type = sample.type;
  }

  const id = `proc-${category.slice(0, 3)}-${Math.random().toString(36).substring(2, 9)}`;

  return {
    id,
    text,
    type,
    category,
  };
}

// ========================================================
// 4. MOTOR DE MAZO BARAJEADO SIN REPETICIONES (NEVER-REPEAT QUEUE)
// ========================================================
// Mantiene colas de frases no vistas por clave (ej: "with-author_all", "without-author_Frases para dedicar")
const unviewedQueues: Record<string, RomanticPhrase[]> = {};
const viewedHistory: Record<string, string[]> = {};

// Barajado Fisher-Yates
function shuffle<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function getPool(mode: PhraseMode, category?: RomanticCategory | 'all'): RomanticPhrase[] {
  const base = mode === 'with-author' ? QUOTES_WITH_AUTHOR : QUOTES_WITHOUT_AUTHOR;
  const filtered = category && category !== 'all'
    ? base.filter((item) => item.category === category)
    : base;

  return filtered.map((item, idx) => ({
    id: `${mode}-${idx}-${Math.random().toString(36).substring(2, 6)}`,
    text: item.text,
    type: item.type,
    category: item.category,
    author: item.author,
  }));
}

/**
 * Retorna una frase garantizando NUNCA REPETIR hasta agotar todo el mazo disponible.
 * Si el mazo se completa, se baraja de nuevo asegurando que la última vista no sea la primera.
 */
export function getRandomInfinitePhrase(
  mode: PhraseMode = 'with-author',
  category: RomanticCategory | 'all' = 'all'
): RomanticPhrase {
  const queueKey = `${mode}_${category}`;

  // Inicializar o recargar la cola si está vacía
  if (!unviewedQueues[queueKey] || unviewedQueues[queueKey].length === 0) {
    let freshPool = getPool(mode, category);
    
    // Si la cola sin autor se agota o para enriquecer, mezclar elementos con variedad
    if (mode === 'without-author') {
      // Agregar un par de procedurales únicos al mazo
      const catToGen = category === 'all' ? 'Frases para dedicar' : category;
      freshPool.push(generateProceduralItem(catToGen));
      freshPool.push(generateProceduralItem(category === 'all' ? 'Frases para enamorar' : catToGen));
      freshPool.push(generateProceduralItem(category === 'all' ? 'Poesías y poemas' : catToGen));
    }

    freshPool = shuffle(freshPool);

    // Evitar que la primera de la nueva vuelta sea idéntica a la última vista
    const lastViewed = viewedHistory[queueKey]?.[viewedHistory[queueKey].length - 1];
    if (lastViewed && freshPool.length > 1 && freshPool[0].text === lastViewed) {
      const temp = freshPool[0];
      freshPool[0] = freshPool[freshPool.length - 1];
      freshPool[freshPool.length - 1] = temp;
    }

    unviewedQueues[queueKey] = freshPool;
  }

  // Tomar el siguiente elemento de la cola barajada
  const nextPhrase = unviewedQueues[queueKey].shift()!;

  // Registrar en historial reciente
  if (!viewedHistory[queueKey]) {
    viewedHistory[queueKey] = [];
  }
  viewedHistory[queueKey].push(nextPhrase.text);
  if (viewedHistory[queueKey].length > 50) {
    viewedHistory[queueKey].shift();
  }

  return nextPhrase;
}
